// --- React · 라우팅 ---
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 ---
import { css } from '@emotion/react';
import { useTheme } from '@emotion/react';
import type { AppTheme } from '@/theme/theme';
import { createAuthStyles } from '@/components/auth/authStyles';
import registerImage from '/images/register.png';

// --- 공통 UI 컴포넌트 ---
import { Input } from '@/components/ui/common/input/Input';
import { LanguageTabs } from '@/components/ui/common/LanguageTabs';
import { Button, ModalShell } from '@/components/ui';
import { ConfiguratorButton } from '@/components/auth';
import { GuideButton } from '@/features/guide';
import { InquiryModal } from '@/components/modals/InquiryModal';

// --- API ---
import { useRegister } from '../../api/auth/useAuth';

// --- 유효성 검사 · 에러 파싱 ---
import { useFormValidation } from '../../hooks/useFormValidation';
import { validateEmail } from '@/utils/validation/email.validator';
import { validatePasswordStrength } from '@/utils/validation/password.validator';
import { AUTH_ERRORS, parseApiError } from '@/utils/errors';
import {
  handleEmailVerificationCheck,
  validateEmailBeforeRegistration,
} from '../../utils/emailVerificationHelpers';

// --- 스토어 ---
import { useNotificationStore } from '../../store/notificationStore';

// --- 타입 ---
import { type RegisterRequest } from '@packages/types/auth/dto';
import { RegistrationStatus } from '@packages/types/auth/registration-status';
import type { ApiError } from '@packages/types/common/dto';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// ==================== 타입 정의 ====================

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

// ==================== 컴포넌트 ====================

function Register() {
  const theme = useTheme() as AppTheme;
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { validateForm } = useFormValidation();
  const { showSuccess, showError } = useNotificationStore();

  // ==================== 상태 관리 ====================

  // 폼 데이터
  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    confirmPassword: '',
  });

  // 이메일 인증 상태
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isLinkSent, setIsLinkSent] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState<string>('');

  // 모달 상태
  const [showAlreadySentModal, setShowAlreadySentModal] = useState(false);
  const [showPendingApprovalModal, setShowPendingApprovalModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);

  // ==================== API 훅 ====================

  const registerMutation = useRegister();
  const hasProcessedEmail = useRef(false);

  // ==================== 이펙트 ====================

  /**
   * URL 파라미터 또는 navigation state에서 인증된 이메일 복원.
   * 이메일 인증 링크 클릭 → 리다이렉트 → 여기서 복원됨.
   */
  useEffect(() => {
    if (hasProcessedEmail.current) return;

    // URL 쿼리 파라미터에서 이메일 확인 (새로고침 시에도 유지)
    const emailFromQuery = searchParams.get('email');
    const verifiedFromQuery = searchParams.get('verified') === 'true';

    // location.state에서 이메일 확인 (직접 네비게이션 시)
    const emailFromState = location.state?.email;
    const verifiedFromState = location.state?.isEmailVerified;

    // 우선순위: state > query parameter
    const email = emailFromState || emailFromQuery;
    const isVerified = verifiedFromState || verifiedFromQuery;

    if (email && isVerified) {
      setFormData(prev => ({ ...prev, email: email }));
      setVerifiedEmail(email);
      setIsEmailVerified(true);
      setIsLinkSent(false);
      hasProcessedEmail.current = true;

      // URL에서 쿼리 파라미터 제거 (깔끔한 URL 유지)
      if (emailFromQuery && !emailFromState) {
        navigate('/register', { replace: true, state: { email, isEmailVerified: true } });
      }
    }
  }, [location.state, searchParams, navigate]);

  // ==================== 핸들러 ====================

  /** 언어 변경 (Login과 동일) */
  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  /**
   * 이메일 인증 링크 발송.
   * 흐름:
   *   1) 이메일 형식 검증
   *   2) 백엔드에 인증 상태 확인 (이미 인증됨/대기중/미등록 등)
   *   3) 상태에 따라 적절한 모달 표시 또는 링크 발송
   */
  const handleSendLink = async (): Promise<void> => {
    // 1) 이메일 형식 검증
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      showError(AUTH_ERRORS.EMAIL_INVALID.title, emailValidation.error!);
      return;
    }

    try {
      // 2-3) 인증 상태 확인 및 처리
      await handleEmailVerificationCheck(
        formData.email,
        setIsEmailVerified,
        setVerifiedEmail,
        setIsLinkSent,
        showSuccess,
        showError,
        navigate,
        t,
        i18n.language,
        () => setShowAlreadySentModal(true),
        () => setShowPendingApprovalModal(true)
      );
    } catch (error: unknown) {
      const errorMessage = parseApiError(error);
      showError(errorMessage.title, errorMessage.message);
    }
  };

  /**
   * 회원가입 폼 제출.
   * 흐름:
   *   1) 이메일 인증 여부 확인
   *   2) 폼 유효성 검사
   *   3) 비밀번호 강도 검증
   *   4) 비밀번호 일치 확인
   *   5) 이메일 인증 상태 재확인 (만료 방지)
   *   6) API 호출 → 성공 시 로그인 페이지 이동
   */
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    // 1) 이메일 인증 여부 확인
    if (!isEmailVerified) {
      showError(t(i18nKeys.auth.input_error), t(i18nKeys.auth.email_verify_before_signup));
      return;
    }

    // 2) 폼 유효성 검사
    const formDataForValidation: Record<string, string> = {
      email: formData.email,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
    };

    const { isFormValid, errors } = validateForm(formDataForValidation);
    if (!isFormValid) {
      const firstError = Object.values(errors)[0];
      showError(t(i18nKeys.auth.input_error), firstError);
      return;
    }

    // 3) 비밀번호 강도 검증
    const passwordValidation = validatePasswordStrength(formData.password);
    if (!passwordValidation.isValid) {
      showError(AUTH_ERRORS.PASSWORD_WEAK.title, passwordValidation.errors[0]);
      return;
    }

    // 4) 비밀번호 일치 확인
    if (formData.password !== formData.confirmPassword) {
      showError(AUTH_ERRORS.PASSWORD_MISMATCH.title, AUTH_ERRORS.PASSWORD_MISMATCH.message);
      return;
    }

    // 5) 이메일 인증 상태 재확인 (만료 방지)
    const validationResult = await validateEmailBeforeRegistration(
      formData.email,
      verifiedEmail,
      setIsEmailVerified,
      setIsLinkSent,
      setVerifiedEmail,
      showError,
      t
    );

    if (!validationResult.isValid) {
      return;
    }

    // 6) API 호출
    const emailToUse = verifiedEmail || formData.email;
    if (!emailToUse) {
      showError(i18nKeys.auth.input_error, i18nKeys.auth.email_required);
      return;
    }

    const registerData: RegisterRequest = {
      email: emailToUse,
      password: formData.password,
      name: emailToUse.split('@')[0],
      language: i18n.language.split('-')[0],
    };

    registerMutation.mutate(registerData, {
      onSuccess: () => {
        showSuccess(i18nKeys.auth.registration_complete, i18nKeys.auth.registration_complete);
        navigate('/login');
      },
      onError: (error: unknown) => {
        const apiError = error as ApiError;
        const errorData = apiError.response?.data;

        // 인증 만료된 경우 — 이메일 재인증 유도
        if (
          errorData?.code === RegistrationStatus.VERIFIED_EXPIRED ||
          errorData?.code === RegistrationStatus.WAITING_EXPIRED
        ) {
          showError(
            AUTH_ERRORS.EMAIL_VERIFICATION_EXPIRED.title,
            AUTH_ERRORS.EMAIL_VERIFICATION_EXPIRED.message
          );
          setIsEmailVerified(false);
          setVerifiedEmail('');
          setIsLinkSent(false);
          return;
        }

        const errorMessage = parseApiError(error);
        showError(errorMessage.title, errorMessage.message);
      },
    });
  };

  // ==================== 렌더링 ====================

  const isLoading = registerMutation.isPending;
  const styles = createAuthStyles(registerImage, theme);

  return (
    <div css={styles.container}>
      {/* 왼쪽 섹션 - 브랜드 이미지 (데스크톱 전용) */}
      <div css={styles.leftSection}>
        <div css={styles.leftOverlay} />
        <div css={styles.leftContent}>
          <div css={styles.leftText}>
            <img
              src="/images/bellcloud.svg"
              alt="BellCloud Logo"
              css={css`
                width: 360px;
                height: auto;
                color: white;
                filter: brightness(0) invert(1);
              `}
            />
          </div>
        </div>
      </div>

      {/* 오른쪽 섹션 - 회원가입 폼 */}
      <div css={styles.rightSection}>
        <div css={styles.formContainer}>
          {/* 타이틀 + 언어 선택 */}
          <div
            css={css`
              display: flex;
              gap: 1rem;
              flex-direction: column;
            `}
          >
            <div>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 0.25rem;
                `}
              >
                <h2 css={styles.formTitle}>{t(i18nKeys.auth.sign_up)}</h2>
                <GuideButton guideKey="register" iconOnly iconSize={24} />
              </div>
              <div css={styles.formTitleLine} />
            </div>

            <div
              css={css`
                margin-left: auto;
              `}
            >
              <LanguageTabs
                currentLanguage={i18n.language}
                onLanguageChange={handleLanguageChange}
              />
            </div>
          </div>

          {/* 회원가입 폼 */}
          <form css={styles.form} onSubmit={handleSubmit}>
            <div css={styles.fieldGroup}>
              {/* 이메일 필드 + 인증 버튼 */}
              <div css={styles.field} data-tour="register-email">
                <p>{t(i18nKeys.auth.e_mail)} *</p>
                <div css={styles.inputGroup}>
                  <Input
                    size="md"
                    fullWidth
                    value={formData.email}
                    onChange={value => {
                      setFormData(prev => ({ ...prev, email: value }));
                      if (value !== verifiedEmail) {
                        setIsEmailVerified(false);
                        setVerifiedEmail('');
                        setIsLinkSent(false);
                      } else if (value === verifiedEmail && verifiedEmail) {
                        setIsEmailVerified(true);
                      }
                    }}
                    placeholder={t(i18nKeys.auth.e_mail)}
                    type="email"
                    hint={t(i18nKeys.auth.email_hint)}
                    disabled={isEmailVerified}
                  />

                  {!isEmailVerified && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleSendLink}
                      disabled={isLoading || !formData.email}
                      data-tour="register-send-link"
                    >
                      {isLoading ? t(i18nKeys.auth.sending) : t(i18nKeys.auth.send)}
                    </Button>
                  )}
                </div>
              </div>

              {/* 인증 메일 발송 안내 메시지 */}
              {isLinkSent && !isEmailVerified && (
                <div css={styles.verifiedMessage}>
                  <svg
                    css={css`
                      width: 1rem;
                      height: 1rem;
                      margin-right: 0.5rem;
                    `}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {t(i18nKeys.auth.email_verification_sent)}
                </div>
              )}

              {/* 이메일 인증 완료 메시지 */}
              {isEmailVerified && (
                <div css={styles.verifiedMessage}>
                  <svg
                    css={css`
                      width: 1rem;
                      height: 1rem;
                      margin-right: 0.5rem;
                    `}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {t(i18nKeys.auth.email_verified)}
                </div>
              )}

              {/* 비밀번호 필드 */}
              <div css={styles.field} data-tour="register-password">
                <p>{t(i18nKeys.auth.password)} *</p>
                <Input
                  size="md"
                  value={formData.password}
                  onChange={value => setFormData(prev => ({ ...prev, password: value }))}
                  placeholder={t(i18nKeys.auth.password)}
                  type="password"
                  minLength={8}
                  hint={t(i18nKeys.auth.password_hint)}
                  fullWidth
                  disabled={!isEmailVerified}
                />
              </div>

              {/* 비밀번호 확인 필드 */}
              <div css={styles.field}>
                <p>{t(i18nKeys.auth.confirm_password)} *</p>
                <Input
                  size="md"
                  value={formData.confirmPassword}
                  onChange={value => setFormData(prev => ({ ...prev, confirmPassword: value }))}
                  placeholder={t(i18nKeys.auth.confirm_password)}
                  type="password"
                  minLength={8}
                  hint={t(i18nKeys.auth.password_hint)}
                  fullWidth
                  disabled={!isEmailVerified}
                />
              </div>
            </div>

            {/* 이용약관 동의 체크박스 */}
            <div css={styles.checkboxContainer}>
              <input id="terms" name="terms" type="checkbox" required css={styles.checkbox} />
              <label htmlFor="terms" css={styles.checkboxLabel}>
                {t(i18nKeys.auth.terms_agreement)}
              </label>
            </div>

            {/* 가입 버튼 */}
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading || !isEmailVerified}
              width="100%"
              data-tour="register-submit"
            >
              {isLoading ? t(i18nKeys.auth.signing_up) : t(i18nKeys.auth.sign_up)}
            </Button>
          </form>

          {/* 하단 링크 (로그인 페이지로 이동) */}
          <div css={styles.bottomLinks}>
            <p css={styles.linkText}>
              {t(i18nKeys.auth.already_have_account)}{' '}
              <Link to="/login" css={styles.link}>
                {t(i18nKeys.auth.login)}
              </Link>
            </p>
            <p css={styles.linkText}>
              <button
                type="button"
                css={[
                  styles.link,
                  css`
                    background: none;
                    border: none;
                    padding: 0;
                    cursor: pointer;
                    font-family: inherit;
                    font-size: inherit;
                  `,
                ]}
                onClick={() => setShowInquiryModal(true)}
              >
                {t(i18nKeys.common.inquiry_menu)}
              </button>
            </p>
          </div>

          {/* 로그인 없이 기기 설정 (WebUSB/BLE 지원 브라우저만) */}
          <ConfiguratorButton />
        </div>
      </div>

      {/* ==================== 모달 ==================== */}

      {/* 관리자 승인 대기 안내 모달 */}
      <ModalShell
        isOpen={showPendingApprovalModal}
        title={t(i18nKeys.auth.email_pending_approval_title)}
        onClose={() => setShowPendingApprovalModal(false)}
        size="sm"
        footer={
          <Button variant="primary" onClick={() => setShowPendingApprovalModal(false)}>
            {t(i18nKeys.auth.close)}
          </Button>
        }
      >
        <p style={{ lineHeight: 1.6, color: 'inherit' }}>
          {t(i18nKeys.auth.email_pending_approval)}
        </p>
      </ModalShell>

      {/* 인증 메일 이미 발송됨 안내 모달 */}
      <ModalShell
        isOpen={showAlreadySentModal}
        title={t(i18nKeys.auth.email_sent)}
        onClose={() => setShowAlreadySentModal(false)}
        size="sm"
        footer={
          <Button variant="primary" onClick={() => setShowAlreadySentModal(false)}>
            {t(i18nKeys.auth.close)}
          </Button>
        }
      >
        <p style={{ lineHeight: 1.6, color: 'inherit' }}>{t(i18nKeys.auth.email_already_sent)}</p>
      </ModalShell>

      {/* 문의하기 모달 */}
      <InquiryModal
        isOpen={showInquiryModal}
        onClose={() => setShowInquiryModal(false)}
        source="register"
        prefill={{ email: formData.email }}
      />
    </div>
  );
}

export default Register;
